import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { BreadcrumbContext } from '@/hooks/useBreadcrumbs';

interface BreadcrumbProviderProps {
  children: React.ReactNode;
}

export function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const setLabel = useCallback((path: string, label: string) => {
    setLabels(prev => {
      // Empty label removes the key entirely (used by unmount cleanup) so
      // empty-string keys don't accumulate.
      if (label === '') {
        if (!(path in prev)) return prev;
        const next = { ...prev };
        delete next[path];
        return next;
      }
      if (prev[path] === label) return prev;
      return { ...prev, [path]: label };
    });
  }, []);

  const value = useMemo(() => ({ labels, setLabel }), [labels, setLabel]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/**
 * Hook for pages to set their breadcrumb label.
 *
 * Leaf page (sets label for current URL):
 *   useBreadcrumbLabel('My Site Name');
 *
 * Parent layout (sets label for a parent crumb whose URL ≠ current URL):
 *   useBreadcrumbLabel('My Site Name', `/sites/${siteId}`);
 */
export function useBreadcrumbLabel(label: string | undefined, pathOverride?: string) {
  const location = useLocation();
  const context = React.useContext(BreadcrumbContext);
  const path = pathOverride ?? location.pathname;

  useEffect(() => {
    if (context && label) {
      context.setLabel?.(path, label);
    }
    return () => {
      if (context?.setLabel) {
        context.setLabel(path, '');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, label]);
}
